import React, { useMemo } from 'react';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '@carbon/react';
import { TypeCurveHeader } from 'types/api-responses';
import { formatNumber, format0 } from 'utils/number';
import styles from './summary-table.module.scss';

type SummaryRow = {
  id: string;
  metric: string;
  totalLL: string;
  perFoot: string;
  perMile: string;
};

const toNum = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
};

const fmt = (
  value: string | number | null | undefined,
  decimals: number,
  unit: string
): string =>
  formatNumber(toNum(value), decimals, decimals, 'decimal', true, '--', unit);

const columns: ColumnDef<SummaryRow>[] = [
  { id: 'metric', accessorKey: 'metric', header: '' },
  { id: 'totalLL', accessorKey: 'totalLL', header: 'Total LL' },
  { id: 'perFoot', accessorKey: 'perFoot', header: 'Per Foot' },
  { id: 'perMile', accessorKey: 'perMile', header: 'Per Mile' },
];

export const SummaryTable: React.FC<{
  typeCurveHeader: TypeCurveHeader;
}> = ({ typeCurveHeader }) => {
  const data: SummaryRow[] = useMemo(() => {
    const spacing = typeCurveHeader.horizontalWellSpacingFt;

    return [
      {
        id: 'spacing',
        metric: 'Spacing',
        totalLL: spacing ? `${format0(spacing)} ft` : '--',
        perFoot: '--',
        perMile: '--',
      },
      {
        id: 'oil-eur',
        metric: 'Oil EUR',
        totalLL: fmt(typeCurveHeader.oilEurMbbl30yr, 0, 'Mbo'),
        perFoot: fmt(typeCurveHeader.oilEurMbbl30yrPerFoot, 2, 'Bo/ft'),
        perMile: fmt(typeCurveHeader.oilEurMbbl30yrPerMile, 0, 'Mbo'),
      },
      {
        id: 'gas-eur',
        metric: 'Gas EUR',
        totalLL: fmt(typeCurveHeader.gasEurMmcf30yr, 0, 'Mmcf'),
        perFoot: fmt(typeCurveHeader.gasEurMmcf30yrPerFoot, 2, 'Mcf/ft'),
        perMile: fmt(typeCurveHeader.gasEurMmcf30yrPerMile, 0, 'Mmcf'),
      },
      {
        id: 'water-eur',
        metric: 'Water EUR',
        totalLL: fmt(typeCurveHeader.waterEurMbbl30yr, 0, 'Mbbl'),
        perFoot: fmt(typeCurveHeader.waterEurMbbl30yrPerFoot, 2, 'Bbl/ft'),
        perMile: fmt(typeCurveHeader.waterEurMbbl30yrPerMile, 0, 'Mbbl'),
      },
      {
        id: 'boe6-eur',
        metric: 'BOE6 EUR',
        totalLL: fmt(typeCurveHeader.boe6Eur, 0, 'Mboe6'),
        perFoot: fmt(typeCurveHeader.boe6EurPerFoot, 2, 'Boe6/ft'),
        perMile: fmt(typeCurveHeader.boe6EurPerMile, 0, 'Mboe6'),
      },
      {
        id: 'boe20-eur',
        metric: 'BOE20 EUR',
        totalLL: fmt(typeCurveHeader.boe20Eur, 0, 'Mboe20'),
        perFoot: fmt(typeCurveHeader.boe20EurPerFoot, 2, 'Boe20/ft'),
        perMile: fmt(typeCurveHeader.boe20EurPerMile, 0, 'Mboe20'),
      },
    ];
  }, [typeCurveHeader]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Summary</h3>
      <Table size="sm">
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell, idx) => (
                <TableCell
                  key={cell.id}
                  className={idx === 0 ? styles.metricCell : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
